import { Select as ArkSelect, createListCollection, useSelectContext } from "@ark-ui/react";
import { CircularProgress } from "@mui/material";
import { ComponentPropsWithoutRef, ReactNode, useCallback, useMemo } from "react";
import { useDebounce } from "use-debounce";
import classnames from "classnames";

import Icon from "@/components/icon/icon";
import selectStyles from "@/components/select/select.module.css";
import LayerLegendTooltip from "@/features/shared/widgets/map/controls/layer-legend-tooltip";
import Switch from "@/features/shared/components/switch/Switch";
import {
  ControlDropdown,
  ControlDropdownContent,
  ControlItem,
  LayerToggleDropdownsContainer,
  LayerToggleRow,
  LayerToggleLabel,
  LayerToggleStateCell,
  LegendIconButton,
} from "@/features/shared/widgets/map/map.styles";
import { EnumLayerSetting } from "@/lib/map-layers/layer-settings/enum-layer-setting";
import {
  LayerSetting,
  LayerSettingValue,
  LayerSettingValueSet,
} from "@/lib/map-layers/layer-settings";
import panelCommonStyles from "@/features/shared/widgets/map/controls/panel-common.module.css";
import { useTranslation } from "@/lib/i18n";

/**
 * Custom popover content for the layer-setting dropdown. Matches the shared
 * Select content but renders into `ControlDropdownContent` so long option labels
 * wrap instead of overflowing horizontally (the popover is portaled, so it
 * cannot be reached via the `ControlDropdown` wrapper). These settings have no
 * groups, so the items are rendered flat.
 */
function WrappingSelectContent(props: ComponentPropsWithoutRef<typeof ArkSelect.Content>) {
  const context = useSelectContext();
  return (
    <ControlDropdownContent {...props} data-part="content">
      {context.collection.items.map((option) => (
        <ArkSelect.Item key={option.value} item={option} className={selectStyles.item}>
          <ArkSelect.ItemText>{option.label}</ArkSelect.ItemText>
        </ArkSelect.Item>
      ))}
    </ControlDropdownContent>
  );
}

type LayerToggleProps = {
  title: string;
  settings: LayerSetting[];
  currentSettingValues: LayerSettingValueSet;
  setSettingValue: (settingId: string, settingValue: LayerSettingValue<unknown>) => void;
  isEnabled: boolean;
  isLoading: boolean;
  isReady: boolean;
  isActive: boolean;
  isForecast: boolean;
  toggle: () => void;
  error?: ReactNode;
  renderedLegend?: ReactNode;
  onShowLegendClick?: () => void;
};

const LayerToggle = ({
  title,
  settings,
  currentSettingValues,
  setSettingValue,
  isEnabled,
  isLoading,
  isReady,
  isActive,
  isForecast,
  toggle,
  error,
  renderedLegend,
  onShowLegendClick,
}: LayerToggleProps) => {
  const { t } = useTranslation();
  const [debouncedIsLoading] = useDebounce(isLoading, 50);

  const stateElement = useMemo(() => {
    if (debouncedIsLoading) return <CircularProgress size={20} color="inherit" />;

    if (error) return error;

    return undefined;
  }, [debouncedIsLoading, error]);

  const handleLegendIconClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onShowLegendClick?.();
    },
    [onShowLegendClick],
  );

  const legendIcon =
    renderedLegend != null && onShowLegendClick ? (
      <LayerLegendTooltip
        noWidth
        ariaLabel={t("map.show_legend_aria")}
        title={renderedLegend}
        onTriggerClick={handleLegendIconClick}
        Trigger={LegendIconButton}
      >
        <Icon name="info" size={16} />
      </LayerLegendTooltip>
    ) : null;

  return (
    <ControlItem
      className={classnames(panelCommonStyles.rowDivider)}
      {...{
        isEnabled,
        isReady: isReady && !error,
        isActive,
        isForecast,
      }}
    >
      <LayerToggleRow>
        <LayerToggleLabel onClick={toggle}>{title}</LayerToggleLabel>
        {stateElement && <LayerToggleStateCell>{stateElement}</LayerToggleStateCell>}
        {legendIcon}
        <Switch value={{}} checked={isEnabled} onClick={toggle} />
      </LayerToggleRow>

      <LayerToggleDropdownsContainer>
        {settings
          .filter((it) => it instanceof EnumLayerSetting)
          .map((layerSetting) => {
            const collection = createListCollection({
              items: layerSetting.values.map((option) => ({
                label: option.label,
                value: option.key,
              })),
            });

            return (
              <ControlDropdown
                key={layerSetting.id}
                collection={collection}
                value={currentSettingValues[layerSetting.id]?.key}
                onValueChange={(value) => {
                  const settingValue = layerSetting.findValueByKey(value);
                  if (settingValue) setSettingValue(layerSetting.id, settingValue);
                }}
                onlyLightTheme
                content={<WrappingSelectContent />}
              />
            );
          })}
      </LayerToggleDropdownsContainer>
    </ControlItem>
  );
};

export default LayerToggle;
